import React, { useEffect, useRef } from 'react'
import { QueueListIcon } from '@heroicons/react/24/outline'

import { useTaskList } from '../hooks/useTaskList'
import { useTaskListStore } from '../store/taskListStore'
import TaskListPopover from './TaskListPopover'

/**
 * The header's task-list control: the icon, the running-count badge, and the popover.
 *
 * Mounted on every page, which is what keeps the two job lists polled for the whole session —
 * the badge has to be right on the catalog, where nothing else reads them.
 */
export default function TaskListButton() {
  const { runningCount } = useTaskList()
  const open = useTaskListStore((s) => s.open)
  const toggle = useTaskListStore((s) => s.toggleTaskList)
  const close = useTaskListStore((s) => s.closeTaskList)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onMouseDown = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      close()
      buttonRef.current?.focus()
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close])

  const label = runningCount === 0 ? 'Tasks' : `Tasks, ${runningCount} running`

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        title={label}
        className="relative p-2 rounded-lg text-theme-text-secondary hover:bg-theme-tertiary hover:text-theme-text-primary transition-colors"
      >
        <QueueListIcon className="w-5 h-5" />
        {runningCount > 0 && (
          <span
            data-testid="task-list-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold leading-[1.125rem] text-center"
          >
            {runningCount}
          </span>
        )}
      </button>

      {open && <TaskListPopover onClose={close} />}
    </div>
  )
}
